
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineScale,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   HELPERS
============================================ */

function getServiceImage(image) {
  if (!image || typeof image !== "string") return null;

  const filename = image.trim();

  if (!filename) return null;

  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  const base = String(media || "").replace(/\/+$/, "");
  const path = filename.replace(/^\/+/, "");

  return base ? `${base}/${path}` : `/${path}`;
}

function truncateWords(value, maxWords = 15) {
  if (typeof value !== "string") return "";

  const plain = value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const words = plain.split(" ");

  if (words.length <= maxWords) return plain;

  return `${words.slice(0, maxWords).join(" ")}...`;
}

/* ============================================
   COMPACT SERVICE CARD
============================================ */

function SmallBlogs({ blogs }) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? blogs?.titleEn
    : blogs?.titleAr;

  const description = truncateWords(
    isEnglish
      ? blogs?.textEn
      : blogs?.textAr,
    14
  );

  const imageUrl = getServiceImage(blogs?.Image);

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  return (
    <Link
      to="/ServiceDetails"
      state={{ ServiceData: blogs }}
      onClick={() => window.scrollTo(0, 0)}
      className="
        group flex min-w-0
        items-center gap-3

        rounded-xl

        border border-skin-border
        bg-skin-page/70
        p-2.5

        transition-all duration-300

        hover:border-skin-accent/35
        hover:bg-skin-soft

        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-skin-accent
      "
    >
      {/* ======================================
          IMAGE
      ====================================== */}

      <div
        className="
          flex h-[88px] w-[88px]
          shrink-0 items-center
          justify-center
          overflow-hidden
          rounded-lg

          bg-[#19344D]
        "
      >
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="
              h-full w-full
              object-cover
              transition-transform duration-500
              group-hover:scale-105
            "
          />
        ) : (
          <HiOutlineScale
            size={32}
            aria-hidden="true"
            className="text-[#D7B77F]"
          />
        )}
      </div>

      {/* ======================================
          CONTENT
      ====================================== */}

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">

        <h3
          className="
            font-cairo
            text-sm font-semibold
            leading-[1.7]

            line-clamp-2
            text-skin-heading

            transition-colors duration-300
            group-hover:text-skin-accent
          "
        >
          {title}
        </h3>

        <p
          className="
            body-4
            line-clamp-2
            text-skin-muted
          "
        >
          {description}
        </p>

        <div className="mt-1 flex items-center gap-1.5 text-skin-accent">

          <span className="font-cairo text-xs font-semibold">
            {isEnglish
              ? "Explore"
              : "التفاصيل"}
          </span>

          <HiOutlineArrowUpRight
            size={15}
            aria-hidden="true"
            className="directional-arrow"
          />
        </div>

      </div>
    </Link>
  );
}

export default SmallBlogs;
